import { of, from, concat } from 'rxjs';
import * as R from 'ramda';
import * as Promise from 'bluebird';
import { mergeMap, withLatestFrom, map, filter, catchError, switchMap, debounceTime } from 'rxjs/operators';
import moment from 'moment';
import { combineEpics, ofType } from 'redux-observable';
import { datasources } from '../db/datasources';
import uuid from 'uuid/v1';
import {
  specsheetSetValues,
  specsheetUpdateSpecsheets,
  specsheetSetSpecsheets,
  specsheetSavePhoto,
  specsheetPhotoSaved,
  specsheetPhotoDeleted,
  specsheetSetOpenDropdown,
  specsheetSetThumbCuts,
  specsheetSetThumbCutsError,
  specsheetSetSpecsheet,
  specSheetRemoved,
  specsheetSelect,
  specsheetAddNew,
  types,
} from './specsheet.redux';
import { types as spectreAppTypes } from 'components/spectreApp/spectreApp.redux';
import { takePhoto } from 'components/fileSystem/takePhoto';
import { galleryPhoto } from 'components/fileSystem/galleryPhoto';
import { fileSystem, getPhotoUrl, isDesktop } from 'components/fileSystem';
import { lpad } from '../../utils';
import {
  idx_data_pitch,
  idx_data_pitch_32,
  idx_data_pitch_16p,
  idx_data_pitch_64,
  idx_data_pitch_32p,
  idx_data_hole_sizes,
} from '../db/datafixtures';
import { createSpecsheet } from 'components/specsheet/createSpecsheet';
import { specSheetDecimalValues } from '../../utils';

const getSpecsheetData = specsheetId =>
  new Promise((success, reject) => {
    const { specSheetDS, thumbCutDS } = datasources;

    const { getImageDirectory } = fileSystem();
    const getImages = () =>
      new Promise((success, reject) => {
        getImageDirectory()
          .then(imageDirectoryReader => {
            imageDirectoryReader.createReader().readEntries(directoryEntries => {
              console.log('IMAGE_DIRECTORY', imageDirectoryReader);
              const images = R.pipe(
                R.filter(file => file.name.indexOf(specsheetId) >= 0),
                R.map(file => getPhotoUrl(imageDirectoryReader)(file))
              )(directoryEntries);

              success(images);
            });
          })
          .catch(reject);
      });

    Promise.all([specSheetDS.get(specsheetId), getImages(), thumbCutDS.read({ specsheetId })])
      .then(response => success(response))
      .catch(reject);
  });

const specsheetOpenPageEpic = (action$, state$) =>
  action$.pipe(
    ofType(spectreAppTypes.spectreappCurrentPage),
    filter(({ payload: { currentPage } }) => currentPage === 'specsheet'),
    withLatestFrom(state$),
    mergeMap(([, state]) =>
      from(
        new Promise(success => {
          const lastCustomer = state.spectreApp.lastCustomerInfo.lastCustomer;
          const lastSpecSheet = state.spectreApp.lastCustomerInfo[lastCustomer].lastSpecSheet;
          const { specSheetDS } = datasources;

          specSheetDS.read({ customerId: lastCustomer }).then(specsheets => success({ specsheets, lastSpecSheet }));
        })
      ).pipe(
        map(({ specsheets, lastSpecSheet }) => {
          return specsheetSetSpecsheets({
            specsheets: R.sort((s1, s2) => {
              const updateAtS1 = typeof s1.updated_at === 'string' ? moment(s1.updated_at).valueOf() : s1.updated_at;
              const updateAtS2 = typeof s2.updated_at === 'string' ? moment(s2.updated_at).valueOf() : s2.updated_at;
              console.log('S1', updateAtS1);
              console.log('S2', updateAtS2);
              return s1.updated_at && s2.updated_at ? updateAtS2 - updateAtS1 : false;
            })(specsheets),
            currentSpecSheet: lastSpecSheet,
          });
        }),
        catchError(error => {
          console.warn(error);
          return specsheetSetSpecsheets({
            specsheets: [],
            currentSpecSheet: {},
            images: [],
            thumbCuts: [],
          });
        })
      )
    )
  );

const specsheetHandleFormEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.specsheetHandleForm),
    debounceTime(500),
    withLatestFrom(state$),
    mergeMap(([{ payload: { specsheetValues } }, state]) =>
      from(
        new Promise(success => {
          const specSheet = R.merge(state.specsheet.currentSpecSheet, R.omit(['id'])(specsheetValues));
          const { specSheetDS } = datasources;

          specSheetDS.update(specSheet).then(success);
        })
      ).pipe(
        map(specSheet => {
          return specsheetSetValues({ currentSpecSheet: specSheet });
        })
      )
    )
  );

const specsheetSetValuesEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.specsheetSetValues),
    withLatestFrom(state$),
    mergeMap(([{ payload: { currentSpecSheet } }, state]) => {
      const newSpecsheets = R.pipe(
        R.findIndex(R.propEq('id', currentSpecSheet.id)),
        R.update(R.__, currentSpecSheet, state.specsheet.specsheets)
      )(state.specsheet.specsheets);
      console.log(newSpecsheets);
      return of(
        specsheetUpdateSpecsheets({
          specsheets: R.sort((s1, s2) => {
            const updateAtS1 = typeof s1.updated_at === 'string' ? moment(s1.updated_at).valueOf() : s1.updated_at;
            const updateAtS2 = typeof s2.updated_at === 'string' ? moment(s2.updated_at).valueOf() : s2.updated_at;
            console.log('S1', updateAtS1);
            console.log('S2', updateAtS2);
            return s1.updated_at && s2.updated_at ? updateAtS2 - updateAtS1 : false;
          })(newSpecsheets),
        })
      );
    })
  );

const specsheetTakePhotoEpic = action$ =>
  action$.pipe(
    ofType(types.specsheetTakePhoto),
    mergeMap(() =>
      from(takePhoto()).pipe(
        map(photoData => {
          console.log(photoData);
          return specsheetSavePhoto({ photoData });
        })
      )
    )
  );

const specsheetGalleryPhotoEpic = action$ =>
  action$.pipe(
    ofType(types.specsheetGalleryPhoto),
    mergeMap(() =>
      from(galleryPhoto()).pipe(
        map(photoData => {
          console.log(photoData);
          return specsheetSavePhoto({ photoData });
        })
      )
    )
  );

const specsheetDeletePhotoEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.specsheetDeletePhoto),
    withLatestFrom(state$),
    mergeMap(([_, state]) => {
      const deleteIndex = state.specsheet.images.length === 1 ? 0 : state.specsheet.currentImageIndex;
      const filename = state.specsheet.images[deleteIndex].name;
      const { removeImageFile } = fileSystem();

      return from(removeImageFile(filename)).pipe(
        map(photoData => {
          console.log(photoData);
          return specsheetPhotoDeleted();
        })
      );
    })
  );

const specsheetSavePhotoEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.specsheetSavePhoto),
    withLatestFrom(state$),
    mergeMap(([{ payload: { photoData: { realData, dataType } } }, state]) => {
      const processPhoto = () =>
        new Promise(success => {
          const { currentSpecSheet, images } = state.specsheet;
          const { savebase64AsImageFile, getImageDirectory } = fileSystem();

          let fileName = currentSpecSheet.id + '_0000.png';
          if (images.length > 0 && images[images.length - 1].name) {
            const lastFileNumber =
              images[images.length - 1].name.replace(`${currentSpecSheet.id}_`, '').replace('.png', '') * 1;
            fileName = `${currentSpecSheet.id}_${lpad(lastFileNumber + 1, 4)}.png`;
          }

          savebase64AsImageFile(fileName, realData, dataType).then(file => {
            console.log('Image saved!!');
            success(R.merge(file, { url: getPhotoUrl(state.spectreApp.imageDirectory)(file) }));
            console.log('Image saved success!!');
          });
        });
      return from(processPhoto()).pipe(
        map(photo => {
          console.log('file saved update action');
          return specsheetPhotoSaved({ photo });
        })
      );
    })
  );

const specsheetSVGClickEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.specsheetSVGClick),
    withLatestFrom(state$),
    mergeMap(([{ payload: { openDropdown } }, state]) => {
      return from(
        new Promise(success => {
          if (state.specsheet.tabChange) {
            setTimeout(() => success(openDropdown), 500);
          } else {
            success(openDropdown);
          }
        })
      ).pipe(map(openDropdown => specsheetSetOpenDropdown({ openDropdown })));
    })
  );

const specsheetCalculateThumbCutsEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.specsheetCalculateThumbCuts),
    withLatestFrom(state$),
    mergeMap(([_, state]) => {
      return from(
        new Promise((success, reject) => {
          const currentSpecSheet = state.specsheet.currentSpecSheet;
          if (
            !currentSpecSheet.thm_oval_starting_bit ||
            !currentSpecSheet.thm_oval_width ||
            !currentSpecSheet.thm_oval_degree
          ) {
            reject('No thumb info');
            return;
          }

          const { thumbCutDS } = datasources;
          const lastSpecSheet = currentSpecSheet.id;

          let ovalWidth = 0;
          let startingBit = 0;

          const startingBitData = R.find(R.propEq('size', currentSpecSheet.thm_oval_starting_bit))(idx_data_hole_sizes);

          if (idx_data_hole_sizes.length > 0) {
            startingBit = R.path(['decimal'])(startingBitData) * 1;
          } else {
            reject('No size starting Bit');
            return;
          }

          const ovalWidthData = R.find(R.propEq('size', currentSpecSheet.thm_oval_width))(idx_data_hole_sizes);

          if (ovalWidthData) {
            ovalWidth = R.path(['decimal'])(ovalWidthData) * 1;
          } else {
            reject('No size starting oval width');
            return;
          }

          thumbCutDS.deleteFromFields({ spec_sheet_id: lastSpecSheet }).then(() => {
            let ovalDegree;
            let diff;
            let startV = 0;
            let startH = 0;
            let finalV;
            let finalH;
            let chunks;
            let chunks32;
            let extraCuts;
            let chunksRemain;
            let cut_number = 0;
            let chunkV;
            let chunkH;
            let i;
            let pitchV = 0;
            let pitchH = 0;
            let settingForRev = 1;
            let settingLateral = 1;

            if (!state.settings.appSettings.oval_machine_right_positive) {
              settingForRev = -1;
            }

            if (!state.settings.appSettings.oval_machine_forward_thumb_positive) {
              settingLateral = -1;
            }

            const thumbCutsToSave = [];

            const handMultiplier = currentSpecSheet.hand === 'right_hander' ? 1 : -1;

            const pitch_measurement = state.settings.appSettings.pitch_measurement;
            const pitchDS = R.pathOr(idx_data_pitch, [pitch_measurement])({
              '32': idx_data_pitch_32,
              '16+': idx_data_pitch_16p,
              '64': idx_data_pitch_64,
              '32+': idx_data_pitch_32p,
            });

            if (state.settings.appSettings.pitch_added_to_oval_calculation) {
              if (currentSpecSheet.thm_rev !== '') {
                const thm_rev_decimal = specSheetDecimalValues(pitchDS, currentSpecSheet.thm_rev);
                if (thm_rev_decimal > 0) {
                  pitchV = thm_rev_decimal * -1 * settingForRev;
                }
              }

              if (currentSpecSheet.thm_for !== '') {
                const thm_for_decimal = specSheetDecimalValues(pitchDS, currentSpecSheet.thm_for);
                if (thm_for_decimal > 0) {
                  pitchV = thm_for_decimal * settingForRev;
                }
              }

              if (currentSpecSheet.thm_lat_r !== '') {
                const thm_lat_r_decimal = specSheetDecimalValues(pitchDS, currentSpecSheet.thm_lat_r);
                if (thm_lat_r_decimal > 0) {
                  pitchH = thm_lat_r_decimal * settingLateral;
                }
              }

              if (currentSpecSheet.thm_lat_l !== '') {
                const thm_lat_l_decimal = specSheetDecimalValues(pitchDS, currentSpecSheet.thm_lat_l);
                if (thm_lat_l_decimal > 0) {
                  pitchH = thm_lat_l_decimal * -1 * settingLateral;
                }
              }
            }

            // thumbCutsToSave.push({
            //   cut_number: 1,
            //   v: 0 + pitchV,
            //   h: (0 + pitchH) * handMultiplier,
            //   label: '1',
            //   spec_sheet_id: lastSpecSheet,
            // });

            if (state.settings.appSettings.oval_calculation !== 'manual') {
              //step 1
              ovalDegree = currentSpecSheet.thm_oval_degree * 1;
              if (state.settings.appSettings.oval_calculation === 'horizontal') {
                ovalDegree = 0;
              }
              diff = ovalWidth - startingBit;

              //step 2
              finalV = Math.sin(Math.radians(360 - ovalDegree)) * diff * -1;

              //step 3
              finalH = Math.cos(Math.radians(360 - ovalDegree)) * diff * handMultiplier;

              //starting point
              if (state.settings.appSettings.pitch_center_edge === 'center') {
                startH = finalH / 2;
                startV = (finalV / 2) * -1;
              }

              //step 4
              chunks = Math.round(diff / (1 / 64));
              chunks32 = ~~(chunks / 2);
              extraCuts = chunks % 2;

              console.log('values', diff, diff / (1 / 64), chunks, chunks32, extraCuts);

              for (i = 0; i <= chunks32; i++) {
                chunkV =
                  pitchV - startV + Math.sin(Math.radians(360 - ovalDegree)) * (1 / 32) * i * -1 * settingForRev * -1;
                if (handMultiplier > 0) {
                  chunkH = pitchH - startH + Math.cos(Math.radians(360 - ovalDegree)) * (1 / 32) * i * settingLateral;
                } else {
                  chunkH = pitchH - startH - Math.cos(Math.radians(360 - ovalDegree)) * (1 / 32) * i * settingLateral;
                }

                cut_number++;
                thumbCutsToSave.push({
                  cut_number: cut_number,
                  v: chunkV,
                  h: chunkH,
                  label: '' + cut_number,
                  spec_sheet_id: lastSpecSheet,
                });
              }

              if (extraCuts > 0) {
                chunkV =
                  pitchV - startV + Math.sin(Math.radians(360 - ovalDegree)) * (1 / 64) * chunks * settingForRev * -1;
                if (handMultiplier > 0) {
                  chunkH =
                    pitchH - startH + Math.cos(Math.radians(360 - ovalDegree)) * (1 / 64) * chunks * settingLateral;
                } else {
                  chunkH =
                    pitchH - startH - Math.cos(Math.radians(360 - ovalDegree)) * (1 / 64) * chunks * settingLateral;
                }

                cut_number++;
                thumbCutsToSave.push({
                  cut_number: cut_number,
                  v: chunkV,
                  h: chunkH,
                  label: '' + cut_number,
                  spec_sheet_id: lastSpecSheet,
                });
              }

              Promise.all(thumbCutsToSave.map(thumbCut => thumbCutDS.create(thumbCut))).then(success);
            }
          });
        })
      ).pipe(
        map(thumbCuts => (thumbCuts ? specsheetSetThumbCuts({ thumbCuts, openOvalInfo: true }) : '')),
        catchError(error => of(specsheetSetThumbCutsError({ error })))
      );
    })
  );

const specsheetAddThumbCutEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.specsheetAddThumbCut),
    withLatestFrom(state$),
    mergeMap(([{ payload: { cutType } }, state]) => {
      return from(
        new Promise((success, reject) => {
          const currentSpecSheet = state.specsheet.currentSpecSheet;
          const lastSpecSheet = currentSpecSheet.id;
          const thumbCuts = state.specsheet.thumbCuts;

          const firstCut = R.pipe(
            R.filter(thumbCut => thumbCut.label.indexOf('&#189;') < 0 && thumbCut.label.indexOf('&#188;') < 0),
            R.head
          )(thumbCuts);

          const lastCut = R.pipe(
            R.filter(thumbCut => thumbCut.label.indexOf('&#189;') < 0 && thumbCut.label.indexOf('&#188;') < 0),
            R.last
          )(thumbCuts);
          const cut_number = lastCut ? R.path(['cut_number'])(lastCut) + 1 : 1;

          const { thumbCutDS } = datasources;

          switch (cutType) {
            case 'new':
              const newCutData = {
                cut_number,
                v: 0,
                h: 0,
                label: '' + cut_number,
                spec_sheet_id: lastSpecSheet,
              };

              thumbCutDS.create(newCutData).then(newCutInsert => success(R.append(newCutInsert)(thumbCuts)));
              break;
            case 'half':
              if (lastCut) {
                const halfCutData = {
                  cut_number,
                  v: (lastCut.v * 1 + firstCut.v * 1) / 2,
                  h: (lastCut.h * 1 + firstCut.h * 1) / 2,
                  label: '&#189;',
                  spec_sheet_id: lastSpecSheet,
                };

                thumbCutDS.create(halfCutData).then(halfCutInsert => success(R.append(halfCutInsert)(thumbCuts)));
              } else {
                reject('No cuts');
              }
              break;
            case 'half_back':
              if (lastCut) {
                const halfBackCutData = {
                  cut_number,
                  v: lastCut.v,
                  h: (lastCut.h * 1 + firstCut.h * 1) / 2,
                  label: '&#189; Back',
                  spec_sheet_id: lastSpecSheet,
                };

                thumbCutDS
                  .create(halfBackCutData)
                  .then(halfBackCutInsert => success(R.append(halfBackCutInsert)(thumbCuts)));
              } else {
                reject('No cuts');
              }
              break;
            case 'half_front':
              if (lastCut) {
                const halfFrontCutData = {
                  cut_number,
                  v: (lastCut.v * 1 + firstCut.v * 1) / 2,
                  h: firstCut.h * 1,
                  label: '&#189; Front',
                  spec_sheet_id: lastSpecSheet,
                };

                thumbCutDS
                  .create(halfFrontCutData)
                  .then(halfFrontCutInsert => success(R.append(halfFrontCutInsert)(thumbCuts)));
              } else {
                reject('No cuts');
              }
              break;
            case 'quarter_back':
              if (lastCut) {
                const quarterBackCutData = {
                  cut_number,
                  v: lastCut.v,
                  h: ((lastCut.h * 1 + firstCut.h * 1) * 3) / 4,
                  label: '&#188; Back',
                  spec_sheet_id: lastSpecSheet,
                };

                thumbCutDS
                  .create(quarterBackCutData)
                  .then(quarterBackCutInsert => success(R.append(quarterBackCutInsert)(thumbCuts)));
              } else {
                reject('No cuts');
              }
              break;
            case 'quarter_front':
              if (lastCut) {
                const quarterFrontCutData = {
                  cut_number,
                  v: (lastCut.v * 1 + firstCut.v * 1) / 4,
                  h: firstCut.h * 1,
                  label: '&#188; Front',
                  spec_sheet_id: lastSpecSheet,
                };

                thumbCutDS
                  .create(quarterFrontCutData)
                  .then(quarterFrontCutInsert => success(R.append(quarterFrontCutInsert)(thumbCuts)));
              } else {
                reject('No cuts');
              }
              break;
          }
        })
      ).pipe(
        map(thumbCuts =>
          thumbCuts
            ? specsheetSetThumbCuts({
                thumbCuts,
                openOvalInfo: state.specsheet.isOvalPreviewOpen,
              })
            : ''
        ),
        catchError(error => of(specsheetSetThumbCutsError({ error })))
      );
    })
  );

const specsheetDeleteThumbCutEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.specsheetDeleteThumbCut),
    withLatestFrom(state$),
    mergeMap(([{ payload: { id } }, state]) => {
      return from(
        new Promise((success, reject) => {
          const thumbCuts = state.specsheet.thumbCuts;

          const newThumbCuts = R.pipe(
            R.findIndex(R.propEq('id', id)),
            R.remove(R.__, 1, thumbCuts)
          )(thumbCuts);

          const { thumbCutDS } = datasources;

          thumbCutDS
            .delete(id)
            .then(() => success(newThumbCuts))
            .catch(reject);
        })
      ).pipe(
        map(thumbCuts =>
          thumbCuts
            ? specsheetSetThumbCuts({
                thumbCuts,
                openOvalInfo: state.specsheet.isOvalPreviewOpen,
              })
            : ''
        ),
        catchError(error => of(specsheetSetThumbCutsError({ error })))
      );
    })
  );

const specsheetUpdateThumbCutEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.specsheetUpdateThumbCut),
    withLatestFrom(state$),
    mergeMap(([{ payload: { id, v = 0.0, h = 0.0 } }, state]) => {
      return from(
        new Promise((success, reject) => {
          const thumbCuts = state.specsheet.thumbCuts;
          const thumbCut = R.find(R.propEq('id', id))(thumbCuts);

          if (!R.isNil(v)) {
            const newThumbCut = R.assocPath(['v'], v)(thumbCut);
            const newThumbCuts = R.pipe(
              R.findIndex(R.propEq('id', id)),
              R.update(R.__, newThumbCut, thumbCuts)
            )(thumbCuts);

            const { thumbCutDS } = datasources;

            thumbCutDS
              .update(newThumbCut)
              .then(() => success(newThumbCuts))
              .catch(reject);
          } else if (!R.isNil(h)) {
            const newThumbCut = R.assocPath(['h'], h)(thumbCut);
            const newThumbCuts = R.pipe(
              R.findIndex(R.propEq('id', id)),
              R.update(R.__, newThumbCut, thumbCuts)
            )(thumbCuts);

            const { thumbCutDS } = datasources;

            thumbCutDS
              .update(newThumbCut)
              .then(() => success(newThumbCuts))
              .catch(reject);
          } else {
            reject('Nothing to update');
          }
        })
      ).pipe(
        map(thumbCuts =>
          thumbCuts
            ? specsheetSetThumbCuts({
                thumbCuts,
                openOvalInfo: state.specsheet.isOvalPreviewOpen,
              })
            : ''
        ),
        catchError(error => of(specsheetSetThumbCutsError({ error })))
      );
    })
  );

const specsheetAddNewEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.specsheetAddNew),
    withLatestFrom(state$),
    mergeMap(([_, state]) => {
      return from(
        new Promise((success, reject) => {
          const { specSheetDS } = datasources;
          const specsheets = state.specsheet.specsheets;
          const appSettings = state.settings.appSettings;
          const customerId = state.bowlers.bowler.id;
          const specsheetId = uuid();

          debugger;
          specSheetDS
            .create(
              createSpecsheet(appSettings)({
                localId: specsheetId,
                customerId,
                bowlerHand:
                  state.bowlers.bowler.hand !== ''
                    ? state.bowlers.bowler.hand
                    : state.settings.appSettings.default_hand,
              })
            )
            .then(newSpecsheet => {
              success({ specsheets: R.append(newSpecsheet)(specsheets), currentSpecSheet: specsheetId });
            })
            .catch(reject);
        })
      ).pipe(
        map(({ specsheets, currentSpecSheet }) => {
          return specsheetSetSpecsheets({
            specsheets: R.sort((s1, s2) => {
              const updateAtS1 = typeof s1.updated_at === 'string' ? moment(s1.updated_at).valueOf() : s1.updated_at;
              const updateAtS2 = typeof s2.updated_at === 'string' ? moment(s2.updated_at).valueOf() : s2.updated_at;
              console.log('S1', updateAtS1);
              console.log('S2', updateAtS2);
              return s1.updated_at && s2.updated_at ? updateAtS2 - updateAtS1 : false;
            })(specsheets),
            currentSpecSheet,
          });
        }),
        catchError(error => {
          console.warn(error);
          return specsheetSetSpecsheets({
            specsheets: [],
            currentSpecSheet: {},
          });
        })
      );
    })
  );

const specsheetCloneEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.specsheetClone),
    withLatestFrom(state$),
    mergeMap(([_, state]) => {
      return from(
        new Promise((success, reject) => {
          const { specSheetDS, thumbCutDS } = datasources;
          const specsheets = state.specsheet.specsheets;
          const specsheetToClone = state.specsheet.currentSpecSheet;
          const thumbCuts = state.specsheet.thumbCuts;
          const specsheetId = uuid();

          specSheetDS
            .create(
              R.pipe(
                R.omit(['id', 'created_at', 'updated_at']),
                R.assoc('localId', specsheetId),
                R.assoc('name', `${R.prop('name')(specsheetToClone)} <Clone>`)
              )(specsheetToClone)
            )
            .then(newSpecsheet => {
              Promise.all(
                thumbCuts.map(thumbCut =>
                  thumbCutDS.create(
                    R.pipe(
                      R.omit('id'),
                      R.assoc('spec_sheet_id', specsheetId)
                    )(thumbCut)
                  )
                )
              ).then(() => {
                success({ specsheets: R.append(newSpecsheet)(specsheets), currentSpecSheet: specsheetId });
              });
            });
        })
      ).pipe(
        map(({ specsheets, currentSpecSheet }) => {
          return specsheetSetSpecsheets({
            specsheets: R.sort((s1, s2) => {
              const updateAtS1 = typeof s1.updated_at === 'string' ? moment(s1.updated_at).valueOf() : s1.updated_at;
              const updateAtS2 = typeof s2.updated_at === 'string' ? moment(s2.updated_at).valueOf() : s2.updated_at;
              console.log('S1', updateAtS1);
              console.log('S2', updateAtS2);
              return s1.updated_at && s2.updated_at ? updateAtS2 - updateAtS1 : false;
            })(specsheets),
            currentSpecSheet,
          });
        }),
        catchError(error => {
          console.warn(error);
          return specsheetSetSpecsheets({
            specsheets: [],
            currentSpecSheet: {},
          });
        })
      );
    })
  );

const specsheetSelectEpic = combineEpics(
  action$ =>
    action$.pipe(
      ofType(types.specsheetSetSpecsheets),
      mergeMap(({ payload: { currentSpecSheet } }) => {
        return from(getSpecsheetData(currentSpecSheet)).pipe(
          map(([specsheet, images, thumbCuts]) => specsheetSetSpecsheet({ specsheet, images, thumbCuts }))
        );
      })
    ),
  action$ =>
    action$.pipe(
      ofType(types.specsheetSelect),
      mergeMap(({ payload: { specsheet } }) => {
        return from(getSpecsheetData(specsheet.id)).pipe(
          map(([specsheet, images, thumbCuts]) => specsheetSetSpecsheet({ specsheet, images, thumbCuts }))
        );
      })
    )
);

const specsheetRemoveEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.specSheetRemove),
    mergeMap(({ payload: { specSheet } }) => {
      const { specSheetDS, thumbCutDS } = datasources;
      return from(
        Promise.all([
          specSheetDS.delete(specSheet.id),
          thumbCutDS.deleteFromFields({
            spec_sheet_id: specSheet.id,
          }),
        ])
      ).pipe(
        switchMap(() => {
          const { specsheets, currentSpecSheet } = state$.value.specsheet;
          const filterSpecsheets = specsheets.filter(specsheetTemp => specsheetTemp.id !== specSheet.id);
          if (currentSpecSheet.id !== specSheet.id) {
            return of(specSheetRemoved({ specsheets: filterSpecsheets }));
          } else if (filterSpecsheets.length === 0) {
            return of(specSheetRemoved({ specsheets: [] }));
          } else {
            return concat(
              of(
                specsheetSelect({
                  specsheet: filterSpecsheets[0] || {},
                })
              ),
              of(specSheetRemoved({ specsheets: filterSpecsheets }))
            );
          }
        })
      );
    })
  );

export default combineEpics(
  specsheetOpenPageEpic,
  specsheetHandleFormEpic,
  specsheetTakePhotoEpic,
  specsheetGalleryPhotoEpic,
  specsheetDeletePhotoEpic,
  specsheetSavePhotoEpic,
  specsheetSVGClickEpic,
  specsheetCalculateThumbCutsEpic,
  specsheetAddThumbCutEpic,
  specsheetDeleteThumbCutEpic,
  specsheetUpdateThumbCutEpic,
  specsheetAddNewEpic,
  specsheetCloneEpic,
  specsheetSelectEpic,
  specsheetSetValuesEpic,
  specsheetRemoveEpic
);
